interface CMSEntity<TAttributes> {
  id: number;
  attributes: TAttributes;
}

export type IMedia = CMSEntity<{
  url: string;
  width: number;
  height: number;
  mime: string;
  name?: string;
  alternativeText?: string;
  caption?: string;
}>;

export interface ISeo {
  rel_canonical_slug?: string;
  title_tag_text: string;
  meta_description_tag_text?: string;
  no_index_tag: boolean;
  include_in_site_map: boolean;
  fb_og_tag: boolean;
  twitter_card_tag: boolean;
  image?: {data: IMedia};
}

export interface IRelatedGame {
  game_id: number;
}

export enum IArticleBodyComponent {
  TEXT = 'article-body.text',
  IMAGE = 'article-body.image',
  INLINE_GAME = 'article-body.inline-game',
  VIDEO = 'article-body.video',
  EMBED = 'article-body.embed',
  CTA_BUTTON = 'article-body.cta-button',
}

/**
 * A single node in Strapi's rich-text "blocks" format (the shape returned by
 * the `copy` field on an `article-body.text` component). The format is a tree
 * of nodes discriminated by `type`:
 *   - block nodes: `paragraph`, `heading` (with `level`), `list`
 *     (with `format`), `list-item`, `quote`, `code`, `image`
 *   - inline nodes: `text` (a leaf, with mark flags) and `link` (with `url`)
 * A single permissive interface is used because the renderer dispatches on
 * `type` via a switch, which keeps narrowing simple and avoids heavy casting.
 */
export interface IStrapiBlockNode {
  type:
    | 'paragraph'
    | 'heading'
    | 'list'
    | 'list-item'
    | 'quote'
    | 'code'
    | 'image'
    | 'link'
    | 'text';
  /** Leaf text content (present on `text` nodes). */
  text?: string;
  /** Inline marks (present on `text` nodes). */
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strikethrough?: boolean;
  code?: boolean;
  /** Heading depth 1-6 (present on `heading` nodes). */
  level?: number;
  /** List style (present on `list` nodes). */
  format?: 'ordered' | 'unordered';
  /** Destination (present on `link` nodes). */
  url?: string;
  /** Inlined media (present on `image` nodes). */
  image?: {
    url: string;
    width?: number;
    height?: number;
    alternativeText?: string;
    caption?: string;
    name?: string;
  };
  /** Child nodes (paragraphs, headings, list items, inline text/links, etc.). */
  children?: IStrapiBlockNode[];
}

export interface IArticleBody {
  id?: number;
  __component: IArticleBodyComponent;
  /**
   * Legacy markdown string. Some `article-body.text` components still return
   * markdown here; newer content returns structured nodes in `copy`. The
   * renderer prefers `copy` and falls back to `text` for backward compatibility.
   */
  text?: string;
  /** Strapi rich-text "blocks" content for `article-body.text` components. */
  copy?: IStrapiBlockNode[];
  image?: {data: IMedia};
  game_id?: number;
  image_alt_text?: string;
  video?: {data: IMedia};
  poster_image?: {data: IMedia};
  embed_url?: string;
  embed_type?: 'Youtube' | 'Vimeo';
  display_text?: string;
  alignment?: 'Left' | 'Center' | 'Right';
  url?: string;
}

export type IArticleCategory = CMSEntity<{
  category: string;
}>;

interface IBaseArticleTypeAttributes {
  slug: string;
  title: string;
  body: IArticleBody[];
  show_genre_carousel: boolean;
  related_game_ids: IRelatedGame[];
  seo: ISeo;
  tagline?: string;
  primary_genre?: string;
  createdAt?: string;
  updatedAt?: string;
  publishedAt?: string;
}

export interface IArticleAttributes extends IBaseArticleTypeAttributes {
  article_category: {
    data: IArticleCategory;
  };
}

export type IArticle = CMSEntity<IArticleAttributes>;

export interface ICMSResponse<T> {
  data: T[];
  meta: {pagination: {page: number; pageSize: number; pageCount: number; total: number}};
}

export interface IArticleCategoryMetadata {
  id: number;
  category: string;
  slug: string;
}
